"use client"
import React from "react"
import { breakpoints } from "../../constants/breakpoints"
import { createMedia } from "./createMedia"

const {
  Media: MediaBase,
  SizeProvider,
  MediaContextProvider,
  useSizeContextSelector,
  createMediaStyle,
} = createMedia(breakpoints)

export type Breakpoint = keyof typeof breakpoints

export const getBreakpointPixelValue = (bp: Breakpoint) => {
  return breakpoints[bp]
}

export type MediaProps = Omit<
  React.ComponentProps<typeof MediaBase>,
  "className" | "children"
> & {
  children: React.ReactNode
}

const useLessThanBreakpointContextSelector = (breakpoint: Breakpoint) => {
  return useSizeContextSelector(
    value => !!value.width && value.width < breakpoints[breakpoint],
  )
}

export const useIsLessThanSm = () => useLessThanBreakpointContextSelector("sm")
export const useIsLessThanMd = () => useLessThanBreakpointContextSelector("md")
export const useIsLessThanLg = () => useLessThanBreakpointContextSelector("lg")
export const useIsLessThanXl = () => useLessThanBreakpointContextSelector("xl")
export const useIsLessThan2Xl = () =>
  useLessThanBreakpointContextSelector("2xl")
export const useIsLessThan3Xl = () =>
  useLessThanBreakpointContextSelector("3xl")
export const useIsLessThan4Xl = () =>
  useLessThanBreakpointContextSelector("4xl")
export const useIsLessThan5Xl = () =>
  useLessThanBreakpointContextSelector("5xl")

export const useIsGreaterThanOrEqualToLg = () => !useIsLessThanLg()

export const useIsLessThanBreakpoint = (breakpoint: Breakpoint) => {
  return useLessThanBreakpointContextSelector(breakpoint)
}

// Make styles for injection into the header of the page
export const mediaStyles = createMediaStyle()

function Media({ children, ...rest }: MediaProps) {
  return (
    <MediaBase className="contents" {...rest}>
      {children}
    </MediaBase>
  )
}
/**
 * Recommendation: Avoid rendering multiple children within a Fragment as the
 * child of a Media component
 *
 * Issues with the incorrect classnames being applied to elements may arise
 */
export { Media, MediaContextProvider, SizeProvider }
